// Other people's motion, smooth between the server's pose updates (Grey, 2026-10-18: "NPCs walking look jittery, as if the
// walk is being stopped halfway through"). Poses come in at POSE_HZ; each pawn used to chase its newest one with an easing,
// which lunges after every update and slows before the next: at 15 Hz a steady 2.4 m/s walk showed as a 3.2 -> 1.7 m/s
// pulse four frames long, and the stride (driven from that speed) fluttered with it. Now every pawn keeps a few timed
// samples and is drawn a little behind the newest, on the straight line between the two either side of that moment: the
// speed between updates is the speed it really walked.
//
// The clock: the server stamps each poses message (and a player's entries say how long before that stamp their pose came
// in); the lowest (arrival - stamp) seen is the link's floor, creeping up slowly so a slower link is followed, which gives
// the server's time now. Each pawn is drawn its own delay behind that: enough that the next update is always in before the
// last one is reached. A crewman the ship walks is about one update interval behind (plus the link's jitter); a player,
// whose poses reach the ship between its ticks and sometimes two to a tick, needs more. The delay follows what the pawn's
// updates have needed (quick to grow, slow to shrink) and changes at most a twentieth as fast as time passes, so a change of
// delay shows as a pawn walking a few percent slower or quicker for a moment, never as a stop.

export interface Sample { t: number; x: number; y: number; z: number; yaw: number }

const CREEP = 0.005;                   // ms per ms: the floor rises 5 ms a second unless a packet comes in under it
const JUMP = 1000;                     // a stamp this far off the floor (a replay's seek or pause, a stalled tab): start again

export class PoseClock {
  private floor = NaN; private seenAt = 0; private gap = 1000 / 15; private lastStamp = NaN;
  constructor(hz: number) { this.gap = 1000 / hz; }
  // a poses message stamped `server` (ms) arrived at `local` (performance.now())
  note(server: number, local: number) {
    const o = local - server;
    if (!Number.isFinite(this.floor) || Math.abs(o - this.floor) > JUMP) this.floor = o;
    else this.floor = Math.min(o, this.floor + (local - this.seenAt) * CREEP);
    if (Number.isFinite(this.lastStamp) && server > this.lastStamp) this.gap += (Math.max(20, Math.min(250, server - this.lastStamp)) - this.gap) * 0.05;
    this.lastStamp = server; this.seenAt = local;
  }
  // the server's time now, as near as we can tell (NaN before the first poses)
  now(local: number) { return local - this.floor; }
  interval() { return this.gap; }
}

const KEEP = 8, MARGIN = 8, RATE = 0.05;
export class Track {
  s: Sample[] = [];
  private want = 1000 / 15 + 20; private delay = NaN; private seen = NaN;
  // a new pose for this pawn, true at server time t, arriving when the server's time is `now`. A jump (a lift, a
  // teleport), a stamp out of order (a replay's seek) starts the track again; after a gap (they stood still, so nothing was
  // sent) they are taken to have stood where they were until an update before this one, not to have crept there all along
  push(t: number, x: number, y: number, z: number, yaw: number, interval: number, now: number) {
    const last = this.s[this.s.length - 1];
    if (last && (t <= last.t || (x - last.x) ** 2 + (y - last.y) ** 2 + (z - last.z) ** 2 > 16)) this.s.length = 0;
    else if (last && t - last.t > interval * 2.5) this.s.push({ t: t - interval, x: last.x, y: last.y, z: last.z, yaw: last.yaw });
    else if (last && Number.isFinite(now)) {     // on the move: how far behind we must draw so as never to run out of track before this came in
      const need = Math.min(400, now - last.t + MARGIN);
      this.want += (need - this.want) * (need > this.want ? 0.5 : 0.02);
    }
    this.s.push({ t, x, y, z, yaw });
    if (this.s.length > KEEP) this.s.splice(0, this.s.length - KEEP);
  }
  clear() { this.s.length = 0; }
  latest(): Sample | undefined { return this.s[this.s.length - 1]; }
  // where the pawn is to be drawn, the server's time being `now`: between the samples either side of (now - delay), held
  // at the ends (never guessed beyond the newest: a stop is where they stopped, and a late packet holds a moment rather
  // than overshooting and pulling back)
  at(now: number, out: { x: number; y: number; z: number; yaw: number }): boolean {
    const s = this.s, n = s.length; if (!n) return false;
    if (!Number.isFinite(this.delay)) this.delay = this.want;
    const step = Number.isFinite(this.seen) ? Math.max(0, now - this.seen) * RATE : 0; this.seen = now;
    this.delay += Math.max(-step, Math.min(step, this.want - this.delay));
    const rt = now - this.delay;
    let a = s[0], b = s[0];
    if (!Number.isFinite(rt) || rt >= s[n - 1].t) a = b = s[n - 1];
    else if (rt > s[0].t) { let i = n - 2; while (i > 0 && s[i].t > rt) i--; a = s[i]; b = s[i + 1]; }
    const f = b.t > a.t ? (rt - a.t) / (b.t - a.t) : 1;
    out.x = a.x + (b.x - a.x) * f; out.y = a.y + (b.y - a.y) * f; out.z = a.z + (b.z - a.z) * f;
    const dy = Math.atan2(Math.sin(b.yaw - a.yaw), Math.cos(b.yaw - a.yaw)); out.yaw = a.yaw + dy * f;
    return true;
  }
  delayMs() { return this.delay; }
}
